// https://brianm.me/posts/react-router-search-params/
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

interface ReadOnlyURLSearchParams extends URLSearchParams {
  append: never;
  set: never;
  delete: never;
  sort: never;
}

export function useSearchParams() {
  const { search, pathname } = useLocation();
  const history = useHistory();

  // 1. Keep your read-only memoized params object
  const searchParams = useMemo(
    () => new URLSearchParams(search) as ReadOnlyURLSearchParams,
    [search],
  );

  // 2. Add a setter function that safely formats and pushes changes via history
  const setSearchParams = useCallback(
    (nextParams: Record<string, string> | URLSearchParams) => {
      const mutableParams =
        nextParams instanceof URLSearchParams
          ? new URLSearchParams()
          : new URLSearchParams(search);

      if (nextParams instanceof URLSearchParams) {
        // If they passed a whole URLSearchParams instance, replace the current params
        nextParams.forEach((value, key) => {
          mutableParams.set(key, value);
        });
      } else {
        // If they passed a plain key-value object, loop and set values
        Object.entries(nextParams).forEach(([key, value]) => {
          mutableParams.set(key, value);
        });
      }

      // Update the browser URL without a full page reload
      history.push({
        pathname: pathname,
        search: mutableParams.toString(),
      });
    },
    [search, pathname, history],
  );

  // Return both items as a tuple
  return [searchParams, setSearchParams] as const;
}
